import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
} from '@mui/material';
import { Box } from '@mui/material';
import { flexStyle, textStyle } from '@themes/mixins';
import { rgba } from 'polished';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Button from '../Button';
import { Root } from '../Button/styles';
import Icon from '../Icon';
import { SvgWrapper } from '../Icon/styles';

export const StyledAppBar = styled.div`
  background-color: ${({ theme }) => theme.colors.paper};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  box-shadow: 0 2px 6px
    ${({ theme }) => `
    ${rgba(theme.colors.boxShadow, 0.12)}`};
  border-top: 0;
  position: relative;
  width: auto;
`;
export const ButtonsContainer = styled.div`
  ${flexStyle({
    align: 'center',
    justify: 'center',
    direction: 'row',
    wrap: 'nowrap',
  })};
  gap: 8px;
`;
export const RightButtonsContainer = styled.div`
  ${flexStyle({
    align: 'center',
    justify: 'center',
    direction: 'row',
    wrap: 'nowrap',
  })};
  gap: 12px;
`;
export const StyledNavButton = styled(Button)<{ active: boolean }>`
  background-color: transparent;
  text-decoration: none;

  & ${Root} {
    color: ${({ theme }) => theme.colors.textSecondary};
    padding: 0 12px;
    background-color: transparent;

    ${({ active }) =>
      active &&
      css`
        color: ${({ theme }) => theme.colors.textPrimary};
      `}
  }
  & ${Root}:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background-color: ${({ theme }) => theme.colors.secondaryBackground};
  }
  ${({ active }) =>
    active &&
    css`
      border-bottom: 2px solid ${({ theme }) => theme.colors.textPrimary};
    `}
`;
export const StyledButton = styled(Button)`
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 0 12px;
  background-color: transparent;
  text-decoration: none;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textPrimary};
    background-color: ${({ theme }) => theme.colors.secondaryBackground};
  }
`;
export const StyledListItemIcon = styled(ListItemIcon)`
  color: ${({ theme }) => theme.colors.textSecondary};
`;
export const StyledSubItemIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.textSecondary};
`;
export const StyledListItemText = styled(ListItemText)`
  color: ${({ theme }) => theme.colors.textSecondary};
`;
export const StyledMenu = styled(Menu)`
  & .MuiMenu-paper {
    background-color: ${({ theme }) => theme.colors.background};
  }
`;
export const StyledMenuItem = styled(MenuItem)`
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background-color: ${({ theme }) =>
      theme.colors.secondaryBackground}!important;
  }
  &:hover ${StyledListItemIcon} {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:hover ${StyledListItemText} {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &.Mui-selected {
    background-color: ${({ theme }) =>
      theme.colors.secondaryBackground}!important;

    & ${StyledListItemIcon} {
      color: ${({ theme }) => theme.colors.textPrimary};
    }
    & ${StyledListItemText} {
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  }
`;
export const StyledToolbar = styled(Toolbar)`
  ${flexStyle({
    align: 'center',
    justify: 'space-between',
    direction: 'row',
  })};
`;
export const FilterTabIcon = styled(Icon)<{ isActive?: boolean }>`
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  ${({ isActive }) =>
    isActive &&
    css`
      color: ${({ theme }) => theme.colors.textPrimary};
    `}
`;
export const Spacer = styled.div`
  flex-grow: 1;
`;

export const NavbarScrolledContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
  animation: navbarSlideIn 0.2s ease;

  @keyframes navbarSlideIn {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
export const LogoIcon = styled(Icon)`
  & > ${SvgWrapper} {
    height: auto;
  }
`;
export const HomeLinkContainer = styled.div`
  display: inline-flex;
  margin: 8px 35px 8px 0;
`;
export const Divider = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  width: 100%;
`;
export const StyledAvatarIcon = styled.div`
  ${textStyle({
    color: 'textSecondary',
    lineHeight: 1,
    size: '24px',
    textAlign: 'center',
    weight: 500,
  })};
  ${flexStyle({
    align: 'center',
    justify: 'center',
    direction: 'row',
  })};
  background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  border-radius: 100%;
  cursor: pointer;
  width: 40px;
  height: 40px;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;
export const UserNameContainer = styled.div<{
  hasPermissionForProfilePage: boolean;
}>`
  ${flexStyle({
    align: 'center',
    justify: 'flex-start',
    direction: 'row',
  })};
  padding: 6px 16px;

  ${({ hasPermissionForProfilePage }) =>
    hasPermissionForProfilePage &&
    css`
      &:hover {
        background-color: ${({ theme }) => theme.colors.surfaceSecondary};
        cursor: pointer;

        ${StyledAvatarIcon} {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
        }
      }
    `}
`;
export const UserName = styled.div`
  ${textStyle({
    color: 'textSecondary',
    lineHeight: 1,
    size: '11px',
    textAlign: 'left',
    weight: 400,
  })};
  margin-bottom: 5px;
  padding-left: 8px;
  width: 100%;
`;
export const UserFullName = styled.div`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '13px',
    textAlign: 'left',
    weight: 500,
  })};
  width: 100%;
  padding-left: 8px;
`;

export const CurrencyDropdownContainer = styled(Box)`
  width: 130px;
  margin-left: 8px;
  margin-right: 8px;
`;
